import { api } from "~/utils/api";
import { cn } from "~/utils/fontsConfig";
import { toast } from "react-toastify";
import { Button } from "../../themed/Button";
import Badge from "../../icons/Badge";
import { BadgeUploader } from "../../badge-components/ConfigEventBadges";
import { TbBadge } from "react-icons/tb";
import { LuTrash } from "react-icons/lu";
import Modal from "../../themed/Modal";

type Props = {
    communitySlug: string;
    isOpen: boolean;
    setIsOpen: () => void;
};

const ManageCommunityBadgesDialog = ({
    communitySlug,
    isOpen,
    setIsOpen,
}: Props) => {
    const { data: communityBadges, refetch: refetchCommunityBadges } =
        api.badge.getBadgesByCommunitySlug.useQuery(
            {
                communitySlug: communitySlug ?? "",
            },
            {
                enabled: !!communitySlug,
            },
        );
    const removeBadge = api.badge.removeCommunityDefaultBadgeV3.useMutation();
    const setDefaultBadge = api.badge.toggleAsCommunityDefault.useMutation();
    const addToAllCurrentEvents = api.badge.addToAllCurrentEvents.useMutation();
    const badgeMutation = api.badge.addBadgeToCommunity.useMutation();

    const handleSetDefaultBadge = async (
        badgeId?: string,
        asDefault?: boolean,
    ) => {
        if (!communitySlug || !badgeId || asDefault === undefined) return;
        await setDefaultBadge.mutateAsync({
            communitySlug: communitySlug,
            badgeId: badgeId,
            asDefault,
        });
        refetchCommunityBadges();
    };

    const handleAddBadgeToCommunity = async (badgeId?: string) => {
        if (!communitySlug || !badgeId) return;
        toast.loading("Adding badge to community", { toastId: "add-badge" });
        await badgeMutation.mutateAsync(
            {
                communitySlug: communitySlug,
                badgeId: badgeId,
            },
            {
                onSuccess: () => {
                    toast.success("Badge added to community");
                },
                onError: () => {
                    toast.error("Badge could not be added to community");
                },
            },
        );
        toast.dismiss("add-badge");
        refetchCommunityBadges();
    };

    const handleAddToAllCurrentEvents = async (badgeId?: string) => {
        if (!communitySlug || !badgeId) return;
        toast.loading("Adding badge to events", { toastId: "add-to-events" });
        await addToAllCurrentEvents.mutateAsync(
            {
                badgeId,
                communitySlug,
            },
            {
                onSuccess: () => {
                    toast.success("Badge added to all current events");
                },
                onError: () => {
                    toast.error(
                        "Badge could not be added to all current events",
                    );
                },
            },
        );
        toast.dismiss("add-to-events");
        refetchCommunityBadges();
    };

    return (
        <Modal
            open={isOpen}
            onOpenChange={() => setIsOpen()}
            icon={<TbBadge className="text-[24px]" />}
            title="Manage community badges"
            description="Manage your community badges here. You can set a default community badge that applies to all new events you create in this community. Additionally, you can remove a badge from your community."
            footer={
                <div className="flex w-full">
                    <BadgeUploader
                        communitySlug={communitySlug}
                        handleAddBadge={handleAddBadgeToCommunity}
                        isModal={true}
                    />
                </div>
            }
        >
            {communityBadges && communityBadges.length > 0 && (
                <>
                    <div className="grid grid-cols-1 gap-2 p-1 md:grid-cols-2 lg:grid-cols-3">
                        {communityBadges.map((badge) => {
                            return (
                                <div
                                    className={cn(
                                        "flex flex-col items-center justify-center gap-1",
                                        "flex rounded-md border-2 p-2",
                                        badge.isCommmunityDefault
                                            ? "border-2 border-v2-cta"
                                            : "",
                                    )}
                                    key={badge.id}
                                >
                                    <div className="min-h-[60px]">
                                        <Badge badge={badge} />
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <div className="grid grid-cols-[80%_20%] gap-1">
                                            {badge.isCommmunityDefault ? (
                                                <Button
                                                    variant="destructive"
                                                    className="h-min w-full rounded px-2 py-1 text-[12px]"
                                                    onClick={async () => {
                                                        await handleSetDefaultBadge(
                                                            badge.id,
                                                            false,
                                                        );
                                                    }}
                                                >
                                                    Unset Default
                                                </Button>
                                            ) : (
                                                <Button
                                                    className="h-min w-full rounded px-2 py-1 text-[12px]"
                                                    onClick={async () => {
                                                        await handleSetDefaultBadge(
                                                            badge.id,
                                                            true,
                                                        );
                                                    }}
                                                >
                                                    Set Default
                                                </Button>
                                            )}
                                            <Button
                                                variant="destructive"
                                                className="h-min rounded p-2 text-[11px]"
                                                onClick={async () => {
                                                    await removeBadge.mutateAsync(
                                                        {
                                                            badgeId: badge.id,
                                                            communitySlug,
                                                        },
                                                    );
                                                    refetchCommunityBadges();
                                                }}
                                            >
                                                <LuTrash />
                                            </Button>
                                        </div>
                                        <Button
                                            className="rounded border-2 border-v3-N-300 px-2 py-1 text-[12px]"
                                            variant="secondary"
                                            onClick={() =>
                                                handleAddToAllCurrentEvents(
                                                    badge.id,
                                                )
                                            }
                                        >
                                            Add to all current events
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </Modal>
    );
};

export default ManageCommunityBadgesDialog;
